import type { AutocompleteOption } from '@/shared/ui/form/autocomplete/helpers/types';
import type { MasterRow } from '../master-table/with-deletable-flag';

type Level = 1 | 2 | 3 | 4;

type ParentCodes = {
  level1_code?: string;
  level2_code?: string;
  level3_code?: string;
};

type LevelParams = {
  rows: MasterRow[];
  level: Level;
  parentCodes?: ParentCodes;
};

/** 親レベルのコードが一致するかを判定する */
const matchesParentCodes = ({
  row,
  level,
  parentCodes,
}: {
  row: MasterRow;
  level: Level;
  parentCodes?: ParentCodes;
}): boolean => {
  if (level >= 2 && parentCodes?.level1_code) {
    if (row.level1_code !== parentCodes.level1_code) return false;
  }
  if (level >= 3 && parentCodes?.level2_code) {
    if (row.level2_code !== parentCodes.level2_code) return false;
  }
  if (level >= 4 && parentCodes?.level3_code) {
    if (row.level3_code !== parentCodes.level3_code) return false;
  }
  return true;
};

/**
 * 既存マスタデータから各レベルのユニークオプションを生成する
 *
 * @param target 'code' の場合はコード、'name' の場合は名称をキーにしたオプションを生成
 */
const buildOptions = ({
  rows,
  level,
  parentCodes,
  target,
}: LevelParams & { target: 'code' | 'name' }): AutocompleteOption[] => {
  const codeField = `level${level}_code` as const;
  const nameField = `level${level}_name` as const;
  const primaryField = target === 'code' ? codeField : nameField;

  const filtered = rows.filter((row) => {
    if (!matchesParentCodes({ row, level, parentCodes })) return false;
    return target === 'code' ? !!row[codeField] : !!row[codeField] && !!row[nameField];
  });

  const seen = new Set<string>();
  const options: AutocompleteOption[] = [];

  for (const row of filtered) {
    const primary = row[primaryField];
    if (!primary || seen.has(primary)) continue;
    seen.add(primary);

    const code = row[codeField];
    const name = row[nameField];
    options.push({
      fieldLabel: primary,
      filterLabel: `${code}.${name}`,
      value: primary,
    });
  }

  return options;
};

/**
 * 既存マスタデータから各レベルのユニークコードオプションを生成する
 */
export const buildCodeOptions = (params: LevelParams): AutocompleteOption[] =>
  buildOptions({ ...params, target: 'code' });

/**
 * 既存マスタデータから各レベルのユニーク名称オプションを生成する
 */
export const buildNameOptions = (params: LevelParams): AutocompleteOption[] => {
  return buildOptions({ ...params, target: 'name' });
};

/** 条件に一致する行を検索する */
const findRow = ({
  rows,
  level,
  parentCodes,
  field,
  value,
}: LevelParams & {
  field: 'code' | 'name';
  value: string;
}): MasterRow | undefined => {
  const targetField =
    field === 'code' ? (`level${level}_code` as const) : (`level${level}_name` as const);

  return rows.find(
    (row) => row[targetField] === value && matchesParentCodes({ row, level, parentCodes }),
  );
};

/**
 * 指定コードに対応する名称を既存データから検索する
 */
export const findNameByCode = ({
  rows,
  level,
  code,
  parentCodes,
}: LevelParams & { code: string }): string | undefined => {
  const nameField = `level${level}_name` as const;
  const match = findRow({ rows, level, parentCodes, field: 'code', value: code });
  return match?.[nameField] || undefined;
};

/**
 * 指定名称に対応するコードを既存データから検索する
 */
export const findCodeByName = ({
  rows,
  level,
  name,
  parentCodes,
}: LevelParams & { name: string }): string | undefined => {
  const codeField = `level${level}_code` as const;
  const match = findRow({ rows, level, parentCodes, field: 'name', value: name });
  return match?.[codeField] || undefined;
};

/**
 * 指定コードが既存マスタデータに存在するか判定する
 */
const isExistingCode = ({
  rows,
  level,
  code,
  parentCodes,
}: LevelParams & { code: string }): boolean => {
  if (!code) return false;
  return findRow({ rows, level, parentCodes, field: 'code', value: code }) !== undefined;
};

/**
 * フォーム入力値と既存データから、各レベルの表示要否を計算する
 *
 * 親レベルのコードが既存データに存在する場合、次のレベルの入力が必要（表示する）
 */
export const computeVisibleLevels = ({
  existingRows,
  formData,
}: {
  existingRows: MasterRow[];
  formData: {
    level1_code: string;
    level2_code?: string;
    level3_code?: string;
  };
}): { showLevel2: boolean; showLevel3: boolean; showLevel4: boolean } => {
  const showLevel2 = isExistingCode({
    rows: existingRows,
    level: 1,
    code: formData.level1_code,
  });

  const showLevel3 =
    showLevel2 &&
    !!formData.level2_code &&
    isExistingCode({
      rows: existingRows,
      level: 2,
      code: formData.level2_code,
      parentCodes: { level1_code: formData.level1_code },
    });

  const showLevel4 =
    showLevel3 &&
    !!formData.level3_code &&
    isExistingCode({
      rows: existingRows,
      level: 3,
      code: formData.level3_code,
      parentCodes: {
        level1_code: formData.level1_code,
        level2_code: formData.level2_code,
      },
    });

  return { showLevel2, showLevel3, showLevel4 };
};
